import { APIHandler } from '../api';
import { CallLog } from './CallLog';
import { CometChatCallsException } from './CometChatCallsException';
import { type ErrorModel } from './ErrorModel';

/**
 * Represents a request to fetch call logs.
 */
class CallLogRequest {
  /**
   * The maximum number of call logs to fetch.
   */
  private limit: number = 30;
  /**
   * The total number of pages of call logs.
   */
  private totalPages: number = 0;
  /**
   * The current page of call logs.
   */
  private currentPage: number = 0;
  /**
   * The type of call to filter by.
   */
  private callType: string;
  /**
   * The status of call to filter by.
   */
  private callStatus: string;
  /**
   * Whether the call has a recording or not.
   */
  private hasRecording: boolean;
  /**
   * The category of call to filter by.
   */
  private callCategory: string;
  /**
   * The direction of call to filter by.
   */
  private callDirection: string;
  /**
   * The user ID to filter by.
   */
  private uid: string;
  /**
   * The group ID to filter by.
   */
  private guid: string;
  /**
   * The authentication token to use for the API call.
   */
  private authToken: string;

  /**
   * Whether an API call is currently in progress.
   */
  private inProgress: boolean = false;

  /**
   * Creates a new CallLogRequest instance.
   * @param builder The builder object to use for constructing the request.
   */
  public constructor(builder: CallLogRequestBuilder) {
    this.limit = builder.limit;
    this.callType = builder.callType;
    this.callStatus = builder.callStatus;
    this.hasRecording = builder.hasRecording;
    this.callCategory = builder.callCategory;
    this.callDirection = builder.callDirection;
    this.uid = builder.uid;
    this.guid = builder.guid;
    this.authToken = builder.authToken;
  }

  /**
   * Fetches the next page of call logs.
   * @returns A promise that resolves to an array of CallLog objects, or rejects with a CometChatCallsException if there was an error.
   */
  public fetchNext(): Promise<CallLog[]> {
    return this.makeAPICall(true);
  }

  /**
   * Fetches the previous page of call logs.
   * @returns A promise that resolves to an array of CallLog objects, or an empty array if there are no previous pages, or rejects with a CometChatCallsException if there was an error..
   */
  public fetchPrevious(): Promise<CallLog[] | []> {
    return this.makeAPICall();
  }

  /**
   * Makes an API call to fetch call logs.
   * @param isFetchNext Whether to fetch the next page of call logs.
   * @returns A promise that resolves to an array of CallLog objects, or rejects with a CometChatCallsException if there was an error.
   */
  private makeAPICall(isFetchNext?: boolean): Promise<CallLog[]> {
    return new Promise(async (resolve, reject) => {
      try {
        //TODO: How do I get CallInstance? I need to check if the CCcall is initialized or not.
        const condition = isFetchNext
          ? this.totalPages != 0 && this.totalPages == this.currentPage
          : this.currentPage == 1;
        if (condition) {
          this.inProgress = false;
          resolve([]);
          return;
        }

        if (this.inProgress) {
          this.inProgress = false;
          reject(
            new CometChatCallsException({
              code: 'REQUEST_IN_PROGRESS',
              name: 'REQUEST_IN_PROGRESS',
              message: 'Request in progress.',
              details: {},
            } as ErrorModel),
          );
          return;
        }
        this.inProgress = true;
        if (!this.authToken) {
          this.inProgress = false;
          reject(
            new CometChatCallsException({
              code: 'NOT_ENOUGH_PARAMETERS',
              message:
                "`Auth Token` is required to use the 'fetchNext()' method.",
            }),
          );
          return;
        }
        try {
          let logs = await APIHandler.getCallLogList(
            this.getParams(isFetchNext),
            this.authToken,
          );
          if (logs?.data?.error) {
            this.inProgress = false;
            reject(new CometChatCallsException(logs.data.error as ErrorModel));
            return;
          }
          let callLogList = this.getCallLogList(JSON.stringify(logs.data));
          resolve(callLogList);
          this.inProgress = false;
        } catch (e) {
          this.inProgress = false;
          reject(new CometChatCallsException(e as ErrorModel));
        }
      } catch (err) {
        this.inProgress = false;
        reject(
          new CometChatCallsException({
            code: 'UNKNOWN ERROR',
            message: 'Unknown error',
          }),
        );
      }
    });
  }

  /**
   * Gets the filter parameters for the API call.
   * @param isNext Whether to fetch the next page of call logs.
   * @returns The filter parameters object.
   */
  private getParams(isNext?: boolean): object {
    let filterObject: any = {};
    if (isNext) {
      filterObject['page'] = this.currentPage + 1;
    } else {
      filterObject['page'] = this.currentPage - 1;
    }

    filterObject['perPage'] = this.limit;

    this.hasRecording && (filterObject['hasRecording'] = this.hasRecording);

    this.callStatus && (filterObject['status'] = this.callStatus);

    this.callType && (filterObject['type'] = this.callType);

    this.callCategory && (filterObject['mode'] = this.callCategory);

    this.callDirection && (filterObject['direction'] = this.callDirection);

    this.uid && (filterObject['uid'] = this.uid);

    this.guid && (filterObject['guid'] = this.guid);

    return filterObject;
  }

  /**
   * Parses the API response and returns an array of CallLog objects.
   * @param response The API response string.
   * @returns An array of CallLog objects.
   */
  private getCallLogList(response: string): CallLog[] {
    let callLogList: CallLog[] = [];

    let responseJson = JSON.parse(response);
    let metaObject = responseJson['meta'];

    if (metaObject.pagination) {
      let paginationObject = metaObject.pagination;
      if (paginationObject.total_pages) {
        this.totalPages = paginationObject.total_pages;
        this.currentPage = paginationObject.current_page;
      }
    }

    let array = responseJson.data;

    for (let i = 0; i < array.length; i++) {
      let object = array[i];
      let callLog: CallLog = CallLog.callLogFromJson(object);
      callLogList.push(callLog);
    }

    return callLogList;
  }
}

/**
 * A builder class for creating a request to fetch call logs.
 */
export class CallLogRequestBuilder {
  /** @private */ limit: number = 30;
  /** @private */ callType!: string;
  /** @private */ callStatus!: string;
  /** @private */ hasRecording!: boolean;
  /** @private */ callCategory!: string;
  /** @private */ callDirection!: string;
  /** @private */ uid!: string;
  /** @private */ guid!: string;
  /** @private */ authToken!: string;

  /**
   * Sets the limit of call logs to be fetched.
   * @param limit - The number of call logs to be fetched.
   * @returns The CallLogRequestBuilder object.
   */
  setLimit(limit: number): this {
    this.limit = limit;
    return this;
  }

  /**
   * Sets the type of call to be fetched.
   * @param callType - The type of call to be fetched. Can be either 'video' or 'audio'.
   * @returns The CallLogRequestBuilder object.
   */
  setCallType(callType: 'video' | 'audio'): this {
    this.callType = callType;
    return this;
  }

  /**
   * Sets the status of call to be fetched.
   * @param callStatus - The status of call to be fetched. Can be either 'ongoing', 'busy', 'rejected', 'cancelled', 'ended' or 'missed'.
   * @returns The CallLogRequestBuilder object.
   */
  setCallStatus(
    callStatus:
      | 'ongoing'
      | 'busy'
      | 'rejected'
      | 'cancelled'
      | 'ended'
      | 'missed',
  ): this {
    this.callStatus = callStatus;
    return this;
  }

  /**
   * Sets whether the call has recording or not.
   * @param hasRecording - Whether the call has recording or not.
   * @returns The CallLogRequestBuilder object.
   */
  setHasRecording(hasRecording: boolean): this {
    this.hasRecording = hasRecording;
    return this;
  }

  /**
   * Sets the category of call to be fetched.
   * @param callCategory - The category of call to be fetched. Can be either 'call' or 'meet'.
   * @returns The CallLogRequestBuilder object.
   */
  setCallCategory(callCategory: 'call' | 'meet'): this {
    this.callCategory = callCategory;
    return this;
  }

  /**
   * Sets the direction of call to be fetched.
   * @param callDirection - The direction of call to be fetched. Can be either 'incoming' or 'outgoing'.
   * @returns The CallLogRequestBuilder object.
   */
  setCallDirection(callDirection: 'incoming' | 'outgoing'): this {
    this.callDirection = callDirection;
    return this;
  }

  /**
   * Sets the user ID of the call logs to be fetched.
   * @param uid - The user ID of the call logs to be fetched.
   * @returns The CallLogRequestBuilder object.
   */
  setUid(uid: string): this {
    this.uid = uid;
    return this;
  }

  /**
   * Sets the group ID of the call logs to be fetched.
   * @param guid - The group ID of the call logs to be fetched.
   * @returns The CallLogRequestBuilder object.
   */
  setGuid(guid: string): this {
    this.guid = guid;
    return this;
  }

  /**
   * Sets the authorization token for the request.
   * @param authToken - The authorization token for the request.
   * @returns The CallLogRequestBuilder object.
   */
  setAuthToken(authToken: string): this {
    this.authToken = authToken;
    return this;
  }

  /**
   * Builds and returns the CallLogRequest object.
   * @returns The CallLogRequest object.
   */
  build(): CallLogRequest {
    return new CallLogRequest(this);
  }
}
